import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import Reveal from "@/components/Reveal";
import { resolveImage } from "@/lib/images";

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://smapi.sanmartino26.al'}/api`;

const Block = ({ block }) => {
  if (block.type === "text") {
    return (
      <Reveal>
        <p
          data-testid={`page-block-text-${block.id}`}
          className="text-base md:text-lg text-sm-text leading-[1.95] font-light whitespace-pre-line max-w-3xl"
        >
          {block.text}
        </p>
      </Reveal>
    );
  }
  if (block.type === "image") {
    return (
      <Reveal>
        <figure data-testid={`page-block-image-${block.id}`} className="my-4">
          <div className="overflow-hidden border border-sm-border">
            <img
              src={resolveImage(block.url)}
              alt={block.alt || ""}
              className="w-full h-auto"
              loading="lazy"
            />
          </div>
          {block.caption && (
            <figcaption className="mt-3 text-sm text-sm-muted italic font-light">
              {block.caption}
            </figcaption>
          )}
        </figure>
      </Reveal>
    );
  }
  if (block.type === "quote") {
    return (
      <Reveal>
        <blockquote
          data-testid={`page-block-quote-${block.id}`}
          className="border-l-2 border-sm-terra pl-8 py-4 my-6 max-w-3xl"
        >
          <p className="font-serif italic text-2xl md:text-3xl text-sm-text leading-snug">
            "{block.text}"
          </p>
          {block.attribution && (
            <cite className="block mt-4 text-[0.7rem] tracking-[0.28em] uppercase text-sm-olive not-italic">
              — {block.attribution}
            </cite>
          )}
        </blockquote>
      </Reveal>
    );
  }
  return null;
};

const CustomPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setPage(null);
    setError(null);
    axios
      .get(`${API}/pages/${slug}`)
      .then((res) => {
        if (cancelled) return;
        setPage(res.data);
        document.title = `${res.data.title} · San Martino 26`;
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.status === 404 ? "not_found" : "error");
      });
    axios.get(`${API}/content`).then((res) => {
      if (!cancelled) setContent(res.data);
    }).catch(() => { });
    return () => { cancelled = true; };
  }, [slug]);

  if (error === "not_found") {
    return (
      <main className="min-h-screen bg-transparent text-sm-text">
        <Navigation brand={content?.brand} />
        <div className="sm-container pt-40 pb-32 text-center" data-testid="page-not-found">
          <p className="sm-eyebrow">404</p>
          <h1 className="font-serif text-5xl md:text-6xl mt-6 italic font-light">
            Faqja nuk u gjet
          </h1>
          <p className="text-sm-muted mt-6 max-w-md mx-auto font-light">
            Kjo faqe mund të jetë hequr ose adresa nuk është e saktë.
          </p>
          <Link
            to="/"
            data-testid="page-not-found-home"
            className="inline-flex items-center gap-2 mt-12 text-[0.75rem] tracking-[0.28em] uppercase text-sm-text border-b border-sm-text"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Tek faqja kryesore
          </Link>
        </div>
      </main>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <p className="text-[0.7rem] tracking-[0.32em] uppercase text-sm-olive animate-pulse">
          San Martino 26
        </p>
      </div>
    );
  }

  return (
    <main data-testid="custom-page" className="bg-transparent text-sm-text">
      <Navigation brand={content?.brand} />

      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24">
        {page.hero_image_url ? (
          <div className="absolute inset-x-0 top-0 h-[60vh] overflow-hidden">
            <img
              src={resolveImage(page.hero_image_url)}
              alt={page.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-sm-bg" />
          </div>
        ) : null}
        <div className="relative sm-container">
          {page.eyebrow && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              data-testid="custom-page-eyebrow"
              className={`text-[0.7rem] tracking-[0.32em] uppercase font-medium ${page.hero_image_url ? "text-white/80" : "text-sm-olive"
                }`}
            >
              {page.eyebrow}
            </motion.p>
          )}
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            data-testid="custom-page-title"
            className={`font-serif text-5xl md:text-6xl lg:text-7xl mt-6 leading-[1] max-w-4xl italic font-light ${page.hero_image_url ? "text-white" : "text-sm-text"
              }`}
          >
            {page.title}
          </motion.h1>
        </div>
      </section>

      {/* Blocks */}
      <section className="sm-container pb-32 md:pb-40">
        <div className="space-y-8 md:space-y-10 max-w-4xl">
          {(page.blocks || []).map((b) => (
            <Block key={b.id} block={b} />
          ))}
        </div>
        <div className="mt-24 pt-10 border-t border-sm-border max-w-4xl">
          <Link
            to="/"
            data-testid="custom-page-back"
            className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.28em] uppercase text-sm-muted hover:text-sm-olive transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Tek faqja kryesore
          </Link>
        </div>
      </section>

      {/* Footer */}
      {content && <Footer content={content.footer} />}
      <FloatingButtons number={content?.brand?.whatsapp_number} />
    </main>
  );
};

export default CustomPage;
